import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../Auth/AuthContext";
import { useGroupContext } from "../Groups/useGroupContext";
import { getCurrencySymbol } from "../utils/currencyFormatter";

const TransactionForm = ({
  isEdit = false,
  data = {},
  onSubmit,
  categories = [],
  vendors = [],
  initialCategoryType = "EXPENSE",
  initialRecurring = false,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const { isGroupMode, currentGroupName } = useGroupContext();
  const { userCurrency } = useContext(AuthContext);
  const [vendorInputFocused, setVendorInputFocused] = useState(false);
  const [formData, setFormData] = useState(() => {
    const defaults = {
      date: today,
      vendor: "",
      amount: "",
      category_name: "",
      category_type: initialCategoryType,
      note: "",
      isRecurring: initialRecurring,
      frequency: "MONTHLY",
      recurringcount: "",
      isPermanent: false,
      old_groupId: "",
      old_groupName: "",
      total_amount: "",
      installment_amount: "",
      isInstallments: false,
      number_of_payments: null,
    };

    const merged = { ...defaults, ...data };

    if (merged.total_amount) {
      merged.isInstallments = true;
      let aux = merged.total_amount;
      merged.total_amount = merged.amount;
      merged.amount = aux;
    }

    if (merged.isPermanent) {
      merged.recurringcount = "";
      merged.end_date = "";
      merged.total_amount = "";
    } else {
      merged.recurringcount = merged.number_of_payments;
    }
    return merged;
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    let newErrors = {};
    if (!formData.date) newErrors.date = "Date is required.";
    if (!formData.vendor.trim()) newErrors.vendor = "Vendor is required.";
    if (!formData.amount || parseFloat(formData.amount) <= 0)
      newErrors.amount = "Amount must be greater than zero.";
    if (!formData.category_name.trim())
      newErrors.category_name = "Category is required.";
    if (
      !categories.some(
        (cat) =>
          cat.name === formData.category_name &&
          cat.type === formData.category_type
      )
    )
      newErrors.category_name = "Invalid category.";

    if (formData.isRecurring && !formData.isPermanent) {
      const recurringCount = parseInt(formData.recurringcount);
      if (
        !formData.recurringcount ||
        isNaN(recurringCount) ||
        recurringCount < 2
      ) {
        newErrors.recurringcount = "Must be at least 2 occurrences.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));

      if (name === "isRecurring" && !checked) {
        setFormData((prev) => ({
          ...prev,
          isRecurring: false,
          frequency: "MONTHLY",
          recurringcount: "",
          isPermanent: false,
          end_date: "",
          total_amount: "",
          installment_amount: "",
          number_of_payments: null,
        }));
      }

      if (name === "isPermanent") {
        if (checked) {
          setFormData((prev) => ({
            ...prev,
            isPermanent: true,
            recurringcount: "",
            end_date: "",
            total_amount: "",
            installment_amount: "",
            number_of_payments: null,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            isPermanent: false,
            recurringcount: "2",
          }));
        }
      }

      if (name === "isInstallments") {
        if (checked) {
          setFormData((prev) => ({
            ...prev,
            isInstallments: true,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            isInstallments: false,
            total_amount: "",
            installment_amount: "",
          }));
        }
      }

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "category_type") {
      setFormData((prev) => ({ ...prev, category_name: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        amount: formData.installment_amount
          ? parseFloat(formData.installment_amount)
          : parseFloat(formData.amount),
        total_amount: formData.installment_amount
          ? parseFloat(formData.total_amount)
          : null,
        number_of_payments: formData.isPermanent
          ? null
          : formData.recurringcount,
        recurringcount: formData.isPermanent
          ? ""
          : formData.recurringcount
          ? parseInt(formData.recurringcount, 10)
          : "",
      });
    }
  };

  useEffect(() => {
    if (formData.isRecurring && !formData.isPermanent) {
      if (
        formData.date &&
        formData.recurringcount &&
        parseInt(formData.recurringcount) >= 2
      ) {
        const newEndDate = calculateEndDate(
          formData.date,
          formData.frequency,
          formData.recurringcount
        );
        setFormData((prev) => ({ ...prev, end_date: newEndDate }));

        if (formData.isInstallments) {
          let installmentAmount =
            parseFloat(formData.amount) / parseInt(formData.recurringcount);
          if (installmentAmount.toString().indexOf(".") > 0) {
            installmentAmount =
              Math.round((installmentAmount + Number.EPSILON) * 100) / 100;
          }
          const newAmount = formData.amount;
          setFormData((prev) => ({
            ...prev,
            amount: newAmount,
            total_amount: formData.amount,
            installment_amount: installmentAmount.toString(),
          }));
        }
      }
    }
  }, [
    formData.date,
    formData.frequency,
    formData.recurringcount,
    formData.isPermanent,
    formData.isRecurring,
    formData.isInstallments,
    formData.amount,
  ]);

  const filteredCategories = useMemo(() => {
    return categories
      .filter((cat) => cat.type === formData.category_type)
      .map((cat) => cat.name);
  }, [formData.category_type, categories]);

  const filteredVendors = useMemo(() => {
    return vendors.map((cat) => cat.vendor);
  }, [vendors]);

  const calculateEndDate = (date, frequency, recurringcount) => {
    if (!date || !recurringcount) return null;

    const startDate = new Date(date);
    const count = parseInt(recurringcount, 10) - 1;

    if (isNaN(count) || count <= 0) return null;

    switch (frequency) {
      case "DAILY":
        startDate.setDate(startDate.getDate() + count);
        break;
      case "WEEKLY":
        startDate.setDate(startDate.getDate() + count * 7);
        break;
      case "MONTHLY":
        startDate.setMonth(startDate.getMonth() + count);
        break;
      case "YEARLY":
        startDate.setFullYear(startDate.getFullYear() + count);
        break;
      default:
        return null;
    }
    startDate.setHours(12, 0, 0, 0);
    return startDate.toISOString().split("T")[0];
  };

  return (
    <Paper sx={{ padding: 3, maxWidth: "500px", margin: "auto", mt: 3 }}>
      <Typography variant="h6">
        {isEdit ? "Edit Transaction" : "Add Transaction"}
      </Typography>
      {isGroupMode && (
        <Typography variant="caption" display="block" color="textSecondary">
          for {currentGroupName}
        </Typography>
      )}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Date"
          type="date"
          name="date"
          fullWidth
          value={formData.date || ""}
          onChange={handleChange}
          error={!!errors.date}
          helperText={errors.date}
          placeholder=""
          sx={{ mt: 2 }}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <Autocomplete
          options={filteredVendors}
          value={formData.vendor || ""}
          inputValue={formData.vendor || ""}
          open={vendorInputFocused && formData.vendor.length >= 2}
          filterOptions={(options, { inputValue }) => {
            if (inputValue.length < 2) {
              // If less than 2 characters, only show the current vendor value if it exists
              return formData.vendor && options.includes(formData.vendor)
                ? [formData.vendor]
                : [];
            }
            return options.filter((option) =>
              option.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
          onInputChange={(_, newInputValue) => {
            setFormData((prev) => ({ ...prev, vendor: newInputValue || "" }));
          }}
          onChange={(_, newValue) =>
            setFormData((prev) => ({ ...prev, vendor: newValue || "" }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              name="vendor"
              fullWidth
              onChange={handleChange}
              error={!!errors.vendor}
              helperText={errors.vendor}
              sx={{ mt: 2 }}
              onFocus={() => setVendorInputFocused(true)}
              onBlur={() => setVendorInputFocused(false)}
            />
          )}
        />

        <TextField
          label={`Amount (${getCurrencySymbol(userCurrency)})`}
          type="text"
          name="amount"
          fullWidth
          value={formData.amount || ""}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "" || /^\d*\.?\d*$/.test(value)) {
              setFormData((prev) => ({
                ...prev,
                amount: value,
              }));
            }
          }}
          error={!!errors.amount}
          helperText={errors.amount}
          sx={{ mt: 2 }}
          slotProps={{
            input: {
              inputMode: "decimal",
            },
          }}
        />

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Category Type</InputLabel>
          <Select
            name="category_type"
            value={formData.category_type || "EXPENSE"}
            onChange={handleChange}
            displayEmpty
            label="Category Type"
          >
            <MenuItem value="EXPENSE">Expense</MenuItem>
            <MenuItem value="INCOME">Income</MenuItem>
            <MenuItem value="INVESTMENT">Investment</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          options={filteredCategories}
          value={formData.category_name || ""}
          onChange={(_, newValue) =>
            setFormData((prev) => ({ ...prev, category_name: newValue || "" }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              name="category_name"
              fullWidth
              onChange={handleChange}
              error={!!errors.category_name}
              helperText={errors.category_name}
              sx={{ mt: 2 }}
            />
          )}
        />

        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Checkbox
            name="isRecurring"
            checked={formData.isRecurring || false}
            onChange={handleChange}
          />
          <Typography variant="body2">Recurring Transaction</Typography>
        </Box>

        {formData.isRecurring && (
          <>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel shrink>Frequency</InputLabel>
              <Select
                name="frequency"
                value={formData.frequency || "MONTHLY"}
                onChange={handleChange}
                label="Frequency"
              >
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
                <MenuItem value="YEARLY">Yearly</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <Checkbox
                name="isInstallments"
                checked={formData.isInstallments || false}
                onChange={handleChange}
              />
              <Typography variant="body2">Paying in installments</Typography>
            </Box>

            <TextField
              label="Number of Payments"
              type="text"
              name="recurringcount"
              fullWidth
              value={formData.recurringcount || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d+$/.test(value)) {
                  const numValue = value === "" ? "" : parseInt(value, 10);
                  setFormData((prev) => ({
                    ...prev,
                    recurringcount:
                      numValue === "" ? "" : numValue < 2 ? 2 : numValue,
                  }));
                }
              }}
              error={!!errors.recurringcount}
              helperText={errors.recurringcount}
              disabled={formData.isPermanent}
              sx={{ mt: 2 }}
              slotProps={{
                input: {
                  inputMode: "numeric",
                },
              }}
            />

            {formData.isInstallments && (
              <TextField
                label="Installment Amount"
                type="text"
                name="installment_amount"
                fullWidth
                value={formData.installment_amount || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setFormData((prev) => ({
                      ...prev,
                      installment_amount: value,
                    }));
                  }
                }}
                error={!!errors.installment_amount}
                helperText={errors.installment_amount}
                disabled={!formData.isInstallments || formData.isPermanent}
                sx={{ mt: 2 }}
                slotProps={{
                  input: {
                    inputMode: "decimal",
                  },
                }}
              />
            )}

            <TextField
              label="End Date"
              type="text"
              name="end_date"
              fullWidth
              value={
                formData.end_date
                  ? format(new Date(formData.end_date), "dd-MMM-yyyy")
                  : ""
              }
              disabled={formData.isPermanent}
              sx={{ mt: 2 }}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <Checkbox
                name="isPermanent"
                checked={formData.isPermanent || false}
                onChange={handleChange}
              />
              <Typography variant="body2">
                Permanent Recurring Payment
              </Typography>
            </Box>
          </>
        )}

        <TextField
          label="Note"
          name="note"
          fullWidth
          value={formData.note || ""}
          onChange={handleChange}
          sx={{ mt: 2 }}
        />

        <Button
          type="submit"
          variant="contained"
          size="small"
          fullWidth
          sx={{ mt: 2 }}
        >
          {isEdit ? "Update Transaction" : "Add Transaction"}
        </Button>
      </form>
    </Paper>
  );
};

export default TransactionForm;
